import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import DriverApplicationPanel from '../components/admin/DriverApplicationPanel';
import {
  generateReferralCode,
  nextReferralCodeAttempt,
} from '../lib/referralCode';
import { setDriverApplicationStatus } from '../lib/admin-driver-approval';
import { driverApplicationMatchesSearch } from '../lib/admin-driver-search';
import {
  applicationSourceLabel,
  fetchDriverApplications,
  normalizeDriverApplication,
  notifyDriverApplicationApproved,
  updateDriverRideServices,
} from '../lib/driver-applications';
import { rideServiceLabel } from '../lib/driver-ride-services';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

const DRIVER_PAGE_SIZES = [10, 15, 25];

function buildPageList(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
}

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'deactivated', label: 'Deactivated' },
];

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function formatPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  return phone || '—';
}

function formatLocation(driver) {
  const parts = [driver.city, driver.district, driver.state].filter(Boolean);
  if (!parts.length) return '—';
  return parts.length > 2 ? `${driver.city}, ${driver.state}` : parts.join(', ');
}

function sourceBadgeClass(source) {
  return source === 'app' ? 'admin-source admin-source--app' : 'admin-source admin-source--web';
}

function statusClass(status) {
  return `admin-status admin-status--${status || 'pending'}`;
}

function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [user, setUser] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [rideServices, setRideServices] = useState('outstation');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const selected = useMemo(
    () => drivers.find((item) => item.id === selectedId) || null,
    [drivers, selectedId]
  );

  const loadDrivers = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setError('');

    const { applications, error: fetchError } = await fetchDriverApplications(supabase);

    if (fetchError) {
      setError(fetchError);
      setDrivers([]);
    } else {
      setDrivers(applications);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setSessionChecked(true);
      return undefined;
    }

    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      const nextUser = data.session?.user || null;
      setUser(nextUser);
      setSessionChecked(true);
      if (!nextUser) {
        navigate('/admin/login', { replace: true });
      }
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUser = session?.user || null;
      setUser(nextUser);
      if (!nextUser) {
        navigate('/admin/login', { replace: true });
      }
    });

    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  useEffect(() => {
    if (user) {
      loadDrivers();
    }
  }, [user, loadDrivers]);

  useEffect(() => {
    const selectId = location.state?.selectDriverId;
    if (!selectId || !drivers.length) return;
    const match = drivers.find((d) => d.id === selectId);
    if (match) {
      setSelectedId(selectId);
      setStatusFilter('all');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [drivers, location.pathname, location.state, navigate]);

  useEffect(() => {
    setNotes(selected?.admin_notes || '');
    setRideServices(selected?.ride_services || 'outstation');
  }, [selected]);

  const saveRideServices = async () => {
    if (!selected || !supabase) return;
    setSaving(true);
    setError('');
    const { ok, error: saveError } = await updateDriverRideServices(
      supabase,
      selected.id,
      rideServices
    );
    if (!ok) {
      setError(saveError);
      setSaving(false);
      return;
    }
    setDrivers((current) =>
      current.map((row) =>
        row.id === selected.id ? { ...row, ride_services: rideServices } : row
      )
    );
    setSaving(false);
  };

  const filteredDrivers = useMemo(() => {
    const list = drivers.filter((driver) => {
      const statusOk =
        statusFilter === 'all' ? true : (driver.status || 'pending') === statusFilter;
      if (!statusOk) return false;
      return driverApplicationMatchesSearch(driver, search);
    });
    list.sort((a, b) =>
      String(a.full_name || '').localeCompare(String(b.full_name || ''), undefined, {
        sensitivity: 'base',
      })
    );
    return list;
  }, [drivers, search, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, pageSize]);

  useEffect(() => {
    if (selectedId && !filteredDrivers.some((d) => d.id === selectedId)) {
      setSelectedId(null);
    }
  }, [filteredDrivers, selectedId]);

  const totalPages = Math.max(1, Math.ceil(filteredDrivers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageDrivers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredDrivers.slice(start, start + pageSize);
  }, [filteredDrivers, currentPage, pageSize]);

  const rangeStart = filteredDrivers.length ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, filteredDrivers.length);
  const pageList = buildPageList(currentPage, totalPages);

  const counts = useMemo(() => {
    return drivers.reduce(
      (acc, driver) => {
        const key = driver.status || 'pending';
        acc.all += 1;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      { all: 0, pending: 0, reviewing: 0, approved: 0, rejected: 0, deactivated: 0 }
    );
  }, [drivers]);

  const updateStatus = async (status) => {
    if (!selected || !supabase) return;
    setSaving(true);
    setError('');

    const needsReferral =
      status === 'approved' && !String(selected.referral_code || '').trim();

    let referralCode = needsReferral
      ? generateReferralCode(selected.full_name, selected.phone)
      : null;
    let data = null;
    let updateError = null;

    for (let attempt = 0; attempt < 5; attempt += 1) {
      const result = await setDriverApplicationStatus(supabase, selected.id, {
        status,
        adminNotes: notes,
        referralCode: needsReferral ? referralCode : null,
      });

      data = result.data;
      updateError = result.error ? { message: result.error } : null;

      if (!updateError) break;

      const message = (updateError.message || '').toLowerCase();
      const referralClash =
        needsReferral &&
        message.includes('23505') &&
        message.includes('referral');
      if (!referralClash) break;
      referralCode = nextReferralCodeAttempt(selected.full_name, selected.phone);
    }

    setSaving(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    const normalized = {
      ...data,
      full_name: data.full_name || data.name || 'Driver',
    };
    setDrivers((prev) =>
      prev.map((item) => (item.id === normalized.id ? normalized : item))
    );

    if (status === 'approved') {
      const notify = await notifyDriverApplicationApproved(supabase, data.id);
      if (!notify.ok) {
        setError(
          `Approved, but could not notify the driver (${notify.error}). Deploy notify-driver-application and mail secrets in Supabase.`
        );
      }
    }
  };

  const saveNotes = async () => {
    if (!selected || !supabase) return;
    setSaving(true);
    setError('');

    const { data, error: updateError } = await supabase
      .from('drivers')
      .update({ admin_notes: notes.trim() || null })
      .eq('id', selected.id)
      .select('*')
      .single();

    setSaving(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    setDrivers((prev) => prev.map((item) => (item.id === data.id ? data : item)));
  };

  const logout = async () => {
    await supabase?.auth.signOut();
    navigate('/admin/login', { replace: true });
  };

  const handleDriverUpdated = useCallback((row) => {
    const normalized = normalizeDriverApplication(row);
    setDrivers((prev) => prev.map((d) => (d.id === normalized.id ? normalized : d)));
  }, []);

  const handleStatusChange = (status) => {
    if (status === 'pending' && selected?.status === 'approved') {
      const ok = window.confirm(
        'Move this driver back to Pending? They will need approval again before dispatch.'
      );
      if (!ok) return;
    }
    updateStatus(status);
  };

  if (!sessionChecked) {
    return <div className="admin-loading">Checking admin session…</div>;
  }

  if (!user) {
    return null;
  }

  return (
    <AdminShell
      userEmail={user.email}
      active="drivers"
      onLogout={logout}
      actions={
        <button type="button" className="btn btn--ghost-dark" onClick={loadDrivers}>
          Refresh list
        </button>
      }
    >
      <header className="admin-page-head admin-page-head--compact">
        <h1>
          Driver applications{' '}
          <span className="admin-page-head__hint">
            (Click a row to review documents, set trip types, and approve or reject)
          </span>
        </h1>
      </header>

          <div className="admin-stats">
            {STATUS_FILTERS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`admin-stat admin-stat--${item.value} ${
                  statusFilter === item.value ? 'admin-stat--active' : ''
                }`}
                onClick={() => setStatusFilter(item.value)}
              >
                <strong>{counts[item.value] || 0}</strong>
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {error && (
            <div className="form-alert form-alert--error admin-drivers__error">{error}</div>
          )}

          <div className="admin-drivers-workspace">
            <div className="admin-drivers__panel">
              <div className="admin-drivers-toolbar">
                <div className="driver-search-field">
                  <span className="driver-search__label" id="driver-search-label">
                    Search drivers
                  </span>
                  <label className="driver-search" aria-labelledby="driver-search-label">
                    <svg
                      className="driver-search__glyph"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                      <path
                        d="M20 20l-3.5-3.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    <input
                      className="driver-search__input"
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Name, email, phone, city, license, referral…"
                      autoComplete="off"
                    />
                    {search.trim() ? (
                      <button
                        type="button"
                        className="driver-search__clear"
                        onClick={() => setSearch('')}
                        aria-label="Clear search"
                      >
                        ×
                      </button>
                    ) : null}
                  </label>
                </div>
                <label className="driver-search__page">
                  <span>Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    aria-label="Rows per page"
                  >
                    {DRIVER_PAGE_SIZES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <span>per page</span>
                </label>
              </div>

              <div className="admin-drivers__table-wrap">
                {loading ? (
                  <p className="admin-empty admin-drivers__empty">Loading applications…</p>
                ) : filteredDrivers.length === 0 ? (
                  <p className="admin-empty admin-drivers__empty">
                    No applications match this filter. Try another status or clear search.
                  </p>
                ) : (
                  <table className="admin-drivers-table">
                    <thead>
                      <tr>
                        <th scope="col" className="admin-drivers-table__sort">
                          Driver <span aria-hidden="true">A–Z</span>
                        </th>
                        <th scope="col">Phone</th>
                        <th scope="col">Location</th>
                        <th scope="col">Source</th>
                        <th scope="col">Trip types</th>
                        <th scope="col">Status</th>
                        <th scope="col">Applied</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageDrivers.map((driver) => {
                        const isSelected = selectedId === driver.id;
                        return (
                          <tr
                            key={driver.id}
                            className={`admin-table-row ${
                              isSelected ? 'admin-table-row--selected admin-drivers-table__row--active' : ''
                            }`}
                            tabIndex={0}
                            onClick={() => setSelectedId(driver.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setSelectedId(driver.id);
                              }
                            }}
                          >
                            <td>
                              <strong className="admin-drivers-table__name">
                                {driver.full_name}
                              </strong>
                            </td>
                            <td>{formatPhone(driver.phone)}</td>
                            <td>{formatLocation(driver)}</td>
                            <td>
                              <span className={sourceBadgeClass(driver.signup_source)}>
                                {applicationSourceLabel(driver.signup_source)}
                              </span>
                            </td>
                            <td>{rideServiceLabel(driver.ride_services)}</td>
                            <td>
                              <span className={statusClass(driver.status)}>
                                {driver.status || 'pending'}
                              </span>
                            </td>
                            <td className="admin-drivers-table__date">
                              {formatDate(driver.created_at)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {!loading && filteredDrivers.length > 0 ? (
                <footer className="admin-drivers__footer">
                  <p className="admin-drivers__summary">
                    {filteredDrivers.length === 0 ? (
                      '0 drivers'
                    ) : (
                      <>
                        Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> of{' '}
                        <strong>{filteredDrivers.length}</strong>
                        {statusFilter !== 'all' ? ` (${statusFilter})` : ''} · sorted by name
                      </>
                    )}
                  </p>
                  <nav className="rides-panel__pager" aria-label="Driver list pages">
                    <button
                      type="button"
                      className="rides-panel__page-btn"
                      disabled={currentPage <= 1}
                      onClick={() => setPage(1)}
                    >
                      First
                    </button>
                    <button
                      type="button"
                      className="rides-panel__page-btn"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    {pageList.map((item, index) =>
                      item === '…' ? (
                        <span key={`gap-${index}`} className="rides-panel__ellipsis">
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          className={`rides-panel__page-btn ${
                            item === currentPage ? 'rides-panel__page-btn--active' : ''
                          }`}
                          onClick={() => setPage(item)}
                        >
                          {item}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      className="rides-panel__page-btn"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </button>
                    <button
                      type="button"
                      className="rides-panel__page-btn"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage(totalPages)}
                    >
                      Last
                    </button>
                  </nav>
                </footer>
              ) : null}
            </div>

          </div>

          {selected ? (
            <DriverApplicationPanel
              driver={selected}
              notes={notes}
              onNotesChange={setNotes}
              rideServices={rideServices}
              onRideServicesChange={setRideServices}
              saving={saving}
              onClose={() => setSelectedId(null)}
              onSaveNotes={saveNotes}
              onSaveRideServices={saveRideServices}
              onUpdateStatus={handleStatusChange}
              onDriverUpdated={handleDriverUpdated}
            />
          ) : null}
    </AdminShell>
  );
}

export default AdminDashboard;
